import React, { useMemo, useState } from 'react';
import {
  Badge, Box, Button, FormControl, FormLabel, HStack, IconButton, Menu, MenuButton, MenuItem,
  MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Portal, Select, Text, Tooltip, useDisclosure, useToast, VStack,
} from '@chakra-ui/react';
import {
  FiCopy, FiDownload, FiEdit2, FiEye, FiMoreVertical, FiRepeat, FiCornerUpRight, FiRotateCcw, FiLink,
} from 'react-icons/fi';
import axios from 'axios';
import { NIL, v4 } from 'uuid';
import { QueryStatus, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useConfirm } from './ConfirmProvider';
import { proposalDealAmount } from '../models/pipeline'; // item 1146
import IconProposals from '../assets/Icon-Proposals.svg';
import { Proposal, ProposalStatus, CustomerSubmissionState } from '../models/proposal';
import useProposal from '../hooks/useProposal';
import useProposalsArchived from '../hooks/useProposalsArchived';
import { nextVersionName } from '../utils/proposalNaming';
import { SqlDateTimeToDisplay } from '../utils/SqlTools'; // item 837 — SENT date
import useProjects from '../hooks/useProjects';
import useCurrentUser from '../hooks/useCurrentUser';
import useCompanyRnwy from '../hooks/useCompanyRnwy';
import DocumentGridShell from './DocumentGridShell';
import EnrollSubscriptionModal from './EnrollSubscriptionModal'; // item 737
import LedgerTable, { LedgerColumn, LedgerBulkAction } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import ProjectOptionsByCustomer from './ProjectOptionsByCustomer'; // item 1454
import { LEDGER_W } from './ledgerGrid';
import { isArchivedRow } from '../utils/archived'; // item 714
import StatusPill from './StatusPill';
import ProposalApprovalWizard from './ProposalApprovalWizard';
import ProposalCompletionWizard from './ProposalCompletionWizard';
import ProposalSendControl from './ProposalSendControl';
import openPdf, { previewPdf } from '../utils/openPdf';
import usePortalShares from '../hooks/usePortalShares'; // item 997
import useProposals from '../hooks/useProposals'; // item 1475b — the global index carries projectLeadNames
import useWorkOrders from '../hooks/useWorkOrders'; // item 1475b — WO leads by proposal
import PortalMark from './PortalMark'; // item 997
import { money } from '../utils/money';
import { canArchiveProposal } from '../utils/archiveRules';
import { approverOf } from '../utils/approver';

// item 1473 — a standalone WO the proposal could adopt (server-computed facts for the confirm)
interface AttachableWo {
  id: string; workOrderNumber: string; title?: string | null; status: number; isSalesOrder: boolean;
  createdDate: string; createdBy?: string | null; total: number; proposalTotal: number; amountMatches: boolean;
  liveInvoices: string[]; finalizedInvoices: number; foreignInvoices: number; resultingStatus: number;
}
const STATUS_WORD: Record<number, string> = { 1: 'Approved', 3: 'In Progress', 4: 'Complete' };

// item 547 — Proposals on the shared LEDGER (the accountant/My-Book grammar Chad likes),
// inside the same section shell (DocumentGridShell) as before. Read-only status PILL (no
// manual status dropdown); ONE contextual primary action opens the focused one-doc wizard
// (Approve / Complete / Reopen); quick Duplicate + PDF; an overflow ⋮ for the rest; and
// checkboxes drive BULK archive/void housekeeping only (never bulk approve/convert).

const PROPOSAL_STATUS_LABELS: Record<number, string> = {
  [ProposalStatus.Pending]: 'PENDING',
  [ProposalStatus.Approved]: 'APPROVED',
  [ProposalStatus.Declined]: 'DECLINED',
  // item 548 — once the deposit's invoiced the job is underway; "In Progress" reads better
  // and fits the pill (Chad) — the underlying status stays ApprovedDeposited.
  [ProposalStatus.ApprovedDeposited]: 'IN PROGRESS',
  [ProposalStatus.Complete]: 'COMPLETE',
};
// the label the status PILL shows (→ StatusPill colours it: "In Progress" = blue/progress)
const pillLabel = (s: ProposalStatus): string => (s === ProposalStatus.ApprovedDeposited ? 'In Progress' : ProposalStatus[s]);
// dart (2026-07-16, Chad) — an RNWY (Automated) proposal is engine-priced; its line items aren't
// materialized until approval, so retailProposalTotal is a placeholder. Show the engine's Perpetual
// one-time (the real deal size) for RNWY; Standard proposals use their materialized line total.
const amountOf = (p: Proposal): number => proposalDealAmount(p); // item 1146 — ONE shared calculator
// dart (2026-07-15, Chad) — local yyyy-MM-dd (NOT toISOString, which is UTC and shifts the day).
const localToday = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface ProposalsGridProps {
  data?: Proposal[];
  status: QueryStatus;
  projectId?: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}

const ProposalsGrid: React.FC<ProposalsGridProps> = (props) => {
  const confirm = useConfirm();
  const {
    data, status, projectId, collapse,
  } = props;
  const inProject = !!projectId && projectId !== NIL;
  const {
    tryArchive, tryVoid, voidMany, tryUnvoid, tryChangeStatus, tryUpdate: tryAddProposal, fetchById,
    unvoidingId,
  } = useProposal({ id: NIL });
  const { canModerate, isExecutive, isAdmin } = useCurrentUser();
  // item 1475b — lead names for rows that arrived as raw entities (in-project grid)
  const { data: proposalIndex } = useProposals();
  const { data: woIndex } = useWorkOrders();
  const leadLineFor = (p: Proposal): string | null => {
    let names: string[] = ((p as any).projectLeadNames as string[] | undefined) ?? [];
    if (names.length === 0) names = ((proposalIndex?.index ?? []).find((x: any) => x.id === p.id) as any)?.projectLeadNames ?? [];
    if (names.length === 0) names = ((woIndex?.index ?? []).find((w: any) => w.proposalId === p.id && !w.isVoid) as any)?.leadNames ?? [];
    return names.length > 0 ? `Lead: ${names.join(', ')}` : null;
  };
  const { capsFor } = usePortalShares(); // item 997 — badge rows the customer portal can see
  // item 737 (Chad) — enroll a sold subscription straight from the proposal ⋮ menu
  const [enrollProposalId, setEnrollProposalId] = useState<string | undefined>(undefined);
  // item 553 — archived proposals are low-value: instead of a peer section on the project page,
  // they're just an "Archived" option in this grid's filter (a view of Proposals), with Restore.
  const {
    archived, restore, restoringId, fullyRestore, fullyRestoringId,
  } = useProposalsArchived();
  const { data: projects } = useProjects();
  // item 681 (Chad) — the live Settings CFG, for the "refresh to current settings" duplicate path
  const { rnwy } = useCompanyRnwy();
  const history = useHistory();
  const toast = useToast();

  const [approving, setApproving] = useState<Proposal | undefined>();
  const [completing, setCompleting] = useState<Proposal | undefined>();
  const approvalModal = useDisclosure();
  const completionModal = useDisclosure();
  const copyModal = useDisclosure();
  const [copyId, setCopyId] = useState('');
  const [copyProjectId, setCopyProjectId] = useState('');
  // item 843 (slice C) — move a customer submission to a live project (same customer)
  const queryClient = useQueryClient();
  const moveModal = useDisclosure();
  const [moveId, setMoveId] = useState('');
  const [moveCustomerId, setMoveCustomerId] = useState('');
  const [moveProjectId, setMoveProjectId] = useState('');
  // dart (2026-07-16, Chad) — per-row spinner while a duplicate is in flight
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  // item 681 (Chad) — guided duplicate for engine proposals: keep the quote's frozen CFG, or refresh
  // to the current Settings CFG (current pricing + latest Sources/Equipment lists). Holds the pending id.
  const [dupChoice, setDupChoice] = useState<Proposal | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | string>('all');
  const showingArchived = statusFilter === 'archived';
  // item 550 — "Send to client" opens the REAL email dialog (with a confirm). item 576 — the
  // whole flow (accept-link mint for Pending + status-aware server copy) lives in the shared
  // ProposalSendControl, the SAME control the proposal form footer renders.
  const [sendId, setSendId] = useState('');
  const [sendStatus, setSendStatus] = useState<ProposalStatus | undefined>();
  const [sendSignal, setSendSignal] = useState(0);

  const onPdfError = () => toast({ status: 'error', title: 'Could not open the PDF' });
  const previewProposal = (id: string) => previewPdf(`proposal/${id}/pdf`).catch(onPdfError);
  const downloadProposal = (id: string) => openPdf(`proposal/${id}/pdf`).catch(onPdfError);
  const openCopyModal = (id: string) => { setCopyId(id); setCopyProjectId(''); copyModal.onOpen(); };

  // item 843 (slice C) — Pulse review actions for a customer submission (defined before the column
  // renderers that reference them). refresh the grid + project after a review action.
  const refreshProposals = () => {
    queryClient.invalidateQueries('proposals');
    queryClient.invalidateQueries('project');
  };
  const openMoveModal = (p: Proposal) => {
    setMoveId(p.id);
    setMoveCustomerId(p.customerId);
    setMoveProjectId('');
    moveModal.onOpen();
  };
  const doMove = async () => {
    if (!moveProjectId) return;
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await axios.post(`${apiUrl}proposal/${moveId}/move-to-project`, { projectId: moveProjectId });
      toast({ status: 'success', title: 'Moved to project' });
      refreshProposals();
    } catch {
      toast({ status: 'error', title: 'Move failed', description: 'The proposal was NOT moved.' });
    }
    moveModal.onClose();
  };
  // item 1469 (Chad: "if we mess up, we void, then it allows us to re run") — moderators hand an
  // Approved / In Progress deal back to Pending; the server refuses while a live FINALIZED invoice
  // exists (void it first) and keeps the work order (re-approval reuses it — no duplicate WO).
  const doReopenPending = async (p: Proposal) => {
    if (!(await confirm(
      `Reopen ${p.proposalNumber ?? 'this proposal'} as Pending? The approval flow runs again from the top; the existing work order is kept and reused. Any live finalized invoice must be voided first.`,
      { title: 'Reopen as Pending', confirmLabel: 'Reopen' },
    ))) return;
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await axios.post(`${apiUrl}proposal/${p.id}/reopen`);
      toast({ status: 'success', title: 'Reopened as Pending', description: 'Approve it again to re-run the wizard.' });
      refreshProposals();
    } catch (e: any) {
      const msg = e?.response?.data ?? 'Please try again.';
      toast({
        status: 'error', title: 'Could not reopen', description: typeof msg === 'string' ? msg : 'Please try again.', duration: 8000, isClosable: true,
      });
    }
  };
  // item 1473 (Chad, GVR: "I did not know we could do this pointing and linking stuff") — adopt a
  // STANDALONE work order into a Pending proposal: pick from the project's unlinked WOs, see the
  // money match + where the proposal will land, confirm. Server links WO + its live invoices and
  // stamps the approval by the WO's creator on the WO's date. No wizard, no new WO, no new invoice.
  const [attachFor, setAttachFor] = useState<Proposal | null>(null);
  const [attachRows, setAttachRows] = useState<AttachableWo[]>([]);
  const [attachPick, setAttachPick] = useState<string>('');
  const [attachBusy, setAttachBusy] = useState(false);
  const openAttach = async (p: Proposal) => {
    setAttachFor(p); setAttachRows([]); setAttachPick('');
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      const { data: res } = await axios.get(`${apiUrl}proposal/${p.id}/attachable-work-orders`);
      const rows = (Array.isArray(res) ? res : []) as AttachableWo[];
      setAttachRows(rows);
      const match = rows.find((r) => r.amountMatches && r.foreignInvoices === 0);
      if (match) setAttachPick(match.id);
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not load work orders', description: typeof e?.response?.data === 'string' ? e.response.data : 'Please try again.' });
      setAttachFor(null);
    }
  };
  const doAttach = async () => {
    const p = attachFor; const row = attachRows.find((r) => r.id === attachPick);
    if (!p || !row) return;
    if (!(await confirm(
      `Attach ${row.workOrderNumber} to ${p.proposalNumber ?? 'this proposal'}? ${row.liveInvoices.length ? `Its ${row.liveInvoices.length} live invoice${row.liveInvoices.length === 1 ? '' : 's'} (${row.liveInvoices.join(', ')}) follow${row.liveInvoices.length === 1 ? 's' : ''} it. ` : ''}The proposal will read ${STATUS_WORD[row.resultingStatus] ?? 'Approved'}, approved by ${row.createdBy ?? 'the work order\'s creator'} on ${new Date(row.createdDate).toLocaleDateString()}. Nothing is regenerated, booked or sent.`,
      { title: 'Attach existing work order', confirmLabel: 'Attach' },
    ))) return;
    setAttachBusy(true);
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await axios.post(`${apiUrl}proposal/${p.id}/attach-work-order`, { workOrderId: row.id });
      toast({ status: 'success', title: `${row.workOrderNumber} attached`, description: `${p.proposalNumber ?? 'Proposal'} now reads ${STATUS_WORD[row.resultingStatus] ?? 'Approved'}.` });
      setAttachFor(null);
      refreshProposals();
      queryClient.invalidateQueries('workOrders'); queryClient.invalidateQueries('invoices'); queryClient.invalidateQueries('project');
    } catch (e: any) {
      const msg = e?.response?.data;
      toast({
        status: 'error', title: 'Could not attach', description: typeof msg === 'string' ? msg : 'Please try again.', duration: 8000, isClosable: true,
      });
    } finally { setAttachBusy(false); }
  };
  const doReopen = async (id: string) => {
    if (!(await confirm(
      'Hand this proposal back to the customer for more edits? It’ll return to their portal as an editable draft.',
      { title: 'Reopen for customer edits', confirmLabel: 'Reopen' },
    ))) return;
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await axios.post(`${apiUrl}proposal/${id}/reopen-customer-edit`);
      toast({ status: 'success', title: 'Reopened for the customer' });
      refreshProposals();
    } catch {
      toast({ status: 'error', title: 'Action failed', description: 'Please try again.' });
    }
  };

  // item 553 — restore an archived proposal (moderator-gated) from the Archived filter view.
  // dart (2026-07-16, Chad) — `full` clears BOTH archive + void in one call (a void+archived quote
  // goes straight back to active); otherwise it just un-archives.
  const doRestore = async (p: Proposal, full = false) => {
    try {
      await (full ? fullyRestore(p.id) : restore(p.id));
      toast({ status: 'success', title: `Restored ${p.proposalNumber || p.proposalTitle}.` });
    } catch (e: any) {
      toast({
        status: 'error',
        title: 'Restore failed',
        description: e?.response?.status === 403 ? 'Only an administrator can restore.' : 'Try again.',
      });
    }
  };

  // item 576 — open the ONE shared send flow for this row (ProposalSendControl mints the
  // accept link for a Pending proposal; other statuses send the plain document with
  // status-aware copy from the server)
  // item 1142 (Chad) — flag/unflag THIS deal as an enterprise deploy (team-effort comp)
  const toggleEnterprise = async (p: Proposal) => {
    const next = !(p as any).isEnterprise;
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await axios.post(`${apiUrl}proposal/${p.id}/enterprise`, { isEnterprise: next });
      queryClient.invalidateQueries('proposals');
      toast({
        status: 'success',
        title: next ? 'Flagged as enterprise deploy' : 'Enterprise flag removed',
        description: next ? 'Finalizing its invoices will remind the booker to zero non-sales commissions.' : undefined,
      });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not update the flag', description: String(e?.response?.data ?? 'Try again.') });
    }
  };

  const sendToClient = (p: Proposal) => {
    // item 870 (Chad) — a customer request can't be priced-and-sent from the hidden drafts
    // bucket; assign it to one of the customer's real projects first (queue Assign / move).
    if (p.isCustomerSubmission && (p as any).projectName === 'Customer Submitted Drafts') {
      toast({
        status: 'warning',
        title: 'Assign to a project first',
        description: 'This customer request still lives in the drafts bucket — assign it to one of the customer’s projects (Tools → Customer requests → Assign), then send.',
        isClosable: true,
        duration: 8000,
      });
      return;
    }
    setSendId(p.id);
    setSendStatus(p.status);
    setSendSignal((s) => s + 1);
  };

  const duplicateProposal = async (id: string, refreshCfg = false) => {
    setDuplicatingId(id);
    try {
      const original = await fetchById(id);
      if (!original) return;
      const copy = { ...original };
      copy.id = NIL;
      // item 681 (Chad) — "refresh to current settings": re-seed the CFG snapshot from the LIVE Settings
      // rate card instead of carrying the source's frozen one. Brings in current pricing + the latest
      // Sources/Equipment lists (older snapshots predate those). Venues + scope carry as-is; the user can
      // Regenerate the SOW once they've checked the new Sources/Equipment. Default (false) = exact clone.
      if (refreshCfg && rnwy?.cfgJson) copy.rnwyCfgJson = rnwy.cfgJson;
      // item 1412 (Chad: "updated the settings for the RNWY standard language, duplicated a quote and
      // told it pull in new information not keep old, and it did not update") — "refresh" now ALSO
      // re-seeds the RNWY Standardized Language section from the live Settings default. The exact
      // clone still carries the source's saved language (Settings never rewrites saved proposals).
      if (refreshCfg && rnwy?.standardLanguage) copy.rnwyStandardLanguage = rnwy.standardLanguage;
      // dart (2026-07-15, Chad) — NEXT_EPIC #5: a duplicate is a NEW proposal → date it TODAY (local),
      // not the source's old date (that's why a "new" proposal came in dated days ago).
      copy.date = localToday();
      // dart (2026-07-15, Chad) — versioned naming ("X" → "X V.1" → "X V.2") instead of "COPY of …"
      copy.proposalTitle = nextVersionName(copy.proposalTitle ?? '', (data ?? []).map((p) => p.proposalTitle ?? ''));
      copy.proposalNumber = '';
      copy.customerProjectManagerId = undefined;
      copy.proposalItems.forEach((i) => { i.id = v4(); });
      copy.status = ProposalStatus.Pending;
      copy.isVoid = false;
      copy.isArchived = false;
      copy.isLocked = false;
      await tryAddProposal(copy);
    } catch (e: any) {
      // item 733 (Chad) — surface the SERVER's reason (e.g. "this project is archived…"), not a
      // generic "NOT saved" that hides WHY.
      toast({
        status: 'error',
        title: 'Could not duplicate',
        description: String(e?.response?.data?.errors?.[0] ?? e?.response?.data ?? 'The proposal was not saved.'),
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setDuplicatingId(null);
    }
  };

  // dart (2026-07-15, Chad) — "Export standard proposal": punt an RNWY (engine-priced) proposal into
  // a new editable Standard proposal (translated SKUs + scope-only SOW + Terms 1), versioned.
  // item 1208 (Chad) — a still-PENDING engine original AUTO-VOIDS on export so the deal counts
  // once in every KPI; its row keeps the blue Restore for a v.2 re-export.
  const isEngineProposal = (p: Proposal) => !!p.isRnwy || p.kind === 1 || p.kind === 2;
  const exportStandard = async (p: Proposal) => {
    try {
      const title = nextVersionName(p.proposalTitle ?? '', (data ?? []).map((x) => x.proposalTitle ?? ''));
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      const { data: res } = await axios.post(`${apiUrl}proposal/${p.id}/export-standard`, { title });
      toast({
        status: 'success',
        title: 'Standard proposal created',
        description: res?.sourceVoided
          ? `Exported as "${title}" — the engine original auto-voided so the deal counts once (Restore on its row brings it back).`
          : `Exported as "${title}" — opening it to edit.`,
      });
      if (res?.id) history.push(`/proposal/${res.id}`);
    } catch {
      toast({ status: 'error', title: 'Export failed', description: 'The standard proposal was not created.' });
    }
  };

  // item 555 — the row "Approve" manually approves in-house, bypassing the client's automated
  // approval link. Confirm first so we nudge toward the automated process (Send to client).
  // item 558 — once the client HAS approved via that link (CLIENT OK), there's nothing to
  // nudge toward: skip the confirm and go straight to processing.
  const openApproval = async (p: Proposal) => {
    if (!(p as any).clientApprovedDate) {
      if (!(await confirm("You sure you don't want the customer to approve via our automated process?", { title: 'Approve without sending?', confirmLabel: 'Yes, approve here', cancelLabel: 'No, send to client' }))) return;
    }
    setApproving(p);
    approvalModal.onOpen();
  };
  const openCompletion = (p: Proposal) => { setCompleting(p); completionModal.onOpen(); };
  const tryEdit = (id: string) => history.push(`/proposal/${id}`);

  // the ONE contextual primary action for a row (opens the focused one-doc flow)
  // item 558 — nomenclature: with the client's OK already in hand, our side isn't
  // "approving" anymore — the button reads PROCESS (same action either way).
  const primaryAction = (p: Proposal): { label: string; onClick: () => void } | undefined => {
    if (p.isVoid || isArchivedRow(p)) return undefined;
    // item 1180b — a PAPER ONLY proposal must never offer Approve/Process: approving would
    // mint a real WO for a legacy deal. It's an inert contract source.
    if ((p as any).isContractOnly) return undefined;
    switch (p.status) {
      // item 734 (Chad's matrix) — the Executive role can view/build proposals but can't
      // approve or process them (mirrors SecurityBL.CanApproveProposalAsync); hide the action.
      // item 884 (Chad's UX screenshot) — an UNSENT customer request's primary verb is
      // REVIEW (open it), never Approve: nothing's been priced or sent yet (matches 870's
      // form-side de-collision). Sent submissions and normal quotes keep Approve/Process.
      case ProposalStatus.Pending:
        if (p.isCustomerSubmission && !(p as any).sentDate) {
          return { label: 'Review', onClick: () => tryEdit(p.id) };
        }
        return isExecutive ? undefined : {
          label: (p as any).clientApprovedDate ? 'Process' : 'Approve',
          onClick: () => openApproval(p),
        };
      case ProposalStatus.Approved:
      case ProposalStatus.ApprovedDeposited: return { label: 'Complete', onClick: () => openCompletion(p) };
      case ProposalStatus.Declined: return { label: 'Reopen', onClick: () => tryChangeStatus(p.id, ProposalStatus.Pending) };
      default: return undefined;
    }
  };

  const columns: LedgerColumn<Proposal>[] = useMemo(() => [
    {
      // item 1445 (Chad, after the 1444 invoice fold: "I thought we were doing a pass on all the ledgers") —
      // PROPOSAL = the portal pattern: number bold, title as two-line subtext, then customer · project
      // (outside a project), SENT and the portal chip. The separate NUMBER + NAME columns are gone;
      // this is the ledger's one flexible column with a 300px floor so the whole title shows.
      // Sort by number; search still covers the title (searchText).
      // item 1447 — title + customer · project ride the full-width rowSubline; number + chips here.
      key: 'number',
      header: 'PROPOSAL',
      width: LEDGER_W.id,
      sortValue: (p) => p.proposalNumber ?? '',
      render: (p) => (
        <Box minW={0}>
          <HStack spacing={3} align="center">
            <Text fontWeight="semibold" whiteSpace="nowrap">{p.proposalNumber ?? '—'}</Text>
            {/* item 997/1004 → item 1449 — portal visibility as a glyph beside the number. Same rule
                  as the portal deals list: sent, OR approved-and-beyond, OR the customer's own submission. */}
            {((p as any).sentDate || p.isCustomerSubmission
              || [ProposalStatus.Approved, ProposalStatus.ApprovedDeposited, ProposalStatus.Complete].includes(p.status))
              && capsFor(p.customerId ?? (p as any).customer?.id)?.proposals
                && <PortalMark what="this proposal's status and PDF" />}
          </HStack>
          {/* item 837 (Chad) — SENT stamp so you can tell which proposal was emailed out (mirrors invoices) */}
          {(p as any).sentDate && (
          <Text fontSize="xs" color="blue.300" fontWeight="medium" whiteSpace="nowrap">
            {`SENT · ${SqlDateTimeToDisplay((p as any).sentDate)}`}
          </Text>
          )}
        </Box>
      ),
    },
    {
      // item 1331 (Chad: "do we have room to add a date") — CREATED date; the ledger's default sort
      // (newest on top) so the chevron has a column to sit on. Number order lies for revisions.
      key: 'created',
      header: 'CREATED',
      width: LEDGER_W.created,
      sortValue: (p) => (p as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (p as { lastModifiedDate?: string }).lastModifiedDate ?? '',
      render: (p) => {
        const d = (p as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (p as { lastModifiedDate?: string }).lastModifiedDate;
        return <span style={{ whiteSpace: 'nowrap' }}>{d ? SqlDateTimeToDisplay(d) : '—'}</span>;
      },
    },
    // dart (2026-07-16, Chad) — proposal TYPE: Standard vs Automated (RNWY, kind===1). Automated
    // shows the RNWY logo; Standard is a muted badge.
    {
      key: 'type',
      header: 'TYPE',
      width: LEDGER_W.slot3,
      sortValue: (p) => ((p as any).kind === 1 || (p as any).isRnwy ? 0 : 1), // Automated sorts first
      render: (p) => (((p as any).kind === 1 || (p as any).isRnwy) ? (
        <HStack spacing={1.5} minW={0} title="Automated (RNWY)">
          {/* RNWY wordmark + "Automated" — both subtle/transparent purple, matching the Standard badge */}
          <Badge variant="subtle" colorScheme="purple" fontSize="0.55rem" fontWeight="bold" letterSpacing="0.05em" px={1.5} borderRadius="sm" flexShrink={0}>RNWY</Badge>
          <Badge variant="subtle" colorScheme="purple" fontSize="0.6rem" flexShrink={0}>Automated</Badge>
          {/* item 1142 — enterprise-deploy comp flag */}
          {(p as any).isEnterprise && (
            <Badge variant="solid" colorScheme="orange" fontSize="0.55rem" flexShrink={0}>ENTERPRISE</Badge>
          )}
        </HStack>
      ) : (
        <HStack spacing={1.5} minW={0}>
          <Badge variant="subtle" colorScheme="gray" fontSize="0.6rem">Standard</Badge>
          {/* item 1142 — enterprise flag shows on standard deals too */}
          {(p as any).isEnterprise && (
            <Badge variant="solid" colorScheme="orange" fontSize="0.55rem" flexShrink={0}>ENTERPRISE</Badge>
          )}
        </HStack>
      )),
    },
    // item 547's NAME column (title + customer · project subtitle) folded under the number — item 1445.
    // item 559 → dart (2026-07-16, Chad) — the APPROVED BY column was RETIRED: the approver now shows
    // as a green ✓ badge on the NAME (above) so the name has room. `approverOf` still drives it there.
    {
      key: 'amount', header: 'AMOUNT', width: LEDGER_W.slot4, align: 'right', sortValue: (p) => amountOf(p), render: (p) => money(amountOf(p)),
    },
    {
      key: 'status',
      header: 'STATUS',
      width: LEDGER_W.status,
      sortValue: (p) => p.status,
      // item 553 — every pill the same width (sized to the widest label) so the column
      // reads as one clean vertical stack.
      // dart (2026-07-16, Chad) — state lives here now: the VOID pill + an ARCHIVED tag (shown in the
      // Archived view / on any archived row), so the NAME column stays uncrowded.
      render: (p) => {
        // dart (2026-07-16, Chad) — the approver reads as a rotated rubber STAMP under the status pill
        // (exactly where the invoice FINALIZED stamp sits), first name + last initial to stay compact.
        const approver = approverOf(p as any);
        const stampName = (() => {
          if (!approver) return '';
          const parts = approver.trim().split(/\s+/);
          return (parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0]).toUpperCase();
        })();
        return (
          <VStack align="start" spacing={1} minW={0}>
            <HStack spacing={1.5} minW={0}>
              {/* item 868b (Chad) — "we don't use Pending until we send": an unsent customer
                  request reads IN REVIEW (item 1031 rename); it becomes a normal Pending the
                  moment we send. */}
              {/* item 1180b (Chad: "overlapping… and it still says PENDING") — PAPER ONLY
                  owns the STATUS slot: the proposal is an inert contract source, not a
                  pending deal, so it must not read (or act) Pending. item 1180c — the pill
                  explains itself on hover. */}
              <Tooltip
                isDisabled={!(p as any).isContractOnly}
                label="Paper only — this proposal exists to generate contracts for a legacy (pre-Basix) deal. It counts in NO totals (opportunities, pipeline, Total Book, Sales Bible) and can't be approved or processed — but it stays live so the deal's contracts can be regenerated or added to later."
                placement="top"
                hasArrow
                maxW="340px"
              >
                <Box>
                  <StatusPill
                    label={(() => {
                      if ((p as any).isContractOnly) return 'Paper Only';
                      return p.isCustomerSubmission && !(p as any).sentDate ? 'In Review' : pillLabel(p.status);
                    })()}
                    isVoid={p.isVoid}
                    minW="118px"
                  />
                </Box>
              </Tooltip>
              {isArchivedRow(p) && <Badge colorScheme="orange" fontSize="0.55rem" flexShrink={0}>ARCHIVED</Badge>}
              {/* item 843 (slice C) — flag a customer-built submission.
                  item 884 — compact: the FOR REVIEW pill already carries the state, so the
                  origin chip is just CUSTOMER (it was truncating + colliding with the row
                  action at tight widths). */}
              {p.isCustomerSubmission && (
                <Badge
                  colorScheme={p.customerSubmissionState === CustomerSubmissionState.Submitted ? 'teal' : 'gray'}
                  fontSize="0.55rem"
                  flexShrink={0}
                  title={`Built by a customer in their proposal portal (${p.customerSubmissionState === CustomerSubmissionState.Submitted ? 'submitted' : 'still drafting'})`}
                >
                  CUSTOMER
                </Badge>
              )}
            </HStack>
            {/* item 706 (Chad) — a sold RNWY subscription whose project has no live recurring record yet:
                it still needs enrollment, so flag it here (amber) and gate completion on it. */}
            {p.pendingRecurring && (
              <Badge
                colorScheme="orange"
                variant="solid"
                fontSize="0.5rem"
                letterSpacing="0.08em"
                borderRadius="sm"
                title="A subscription was sold but no recurring billing is enrolled yet"
              >
                PENDING RECURRING
              </Badge>
            )}
            {/* item 788 (Chad) — approved RNWY deal, contracts not prepared yet (same pattern
                as PENDING RECURRING; clears once a contract document exists for this proposal) */}
            {(p as any).pendingContracts && (
              <Badge
                colorScheme="purple"
                variant="solid"
                fontSize="0.5rem"
                letterSpacing="0.08em"
                borderRadius="sm"
                title="No contract has been prepared for this approved deal yet"
              >
                CONTRACTS PENDING
              </Badge>
            )}
            {approver && (
              <Box
                display="inline-block"
                transform="rotate(-6deg)"
                borderWidth="2px"
                borderColor="green.400"
                color="green.400"
                borderRadius="sm"
                px={1.5}
                lineHeight="1.4"
                fontSize="0.55rem"
                fontWeight="extrabold"
                letterSpacing="0.14em"
                opacity={0.85}
                whiteSpace="nowrap"
                userSelect="none"
                title={approver}
              >
                {stampName}
              </Box>
            )}
          </VStack>
        );
      },
    },
  ], [capsFor]); // item 997 — capsFor drives the SHARED TO PORTAL chip

  // item 553 — the trailing icon cluster (Preview + Download) rendered so its columns line up
  // on EVERY row: void rows leave the Duplicate/⋮ slots as fixed-width spacers so Preview and
  // Download sit in the exact same place as on active rows.
  const iconSpacer = <Box w="24px" flexShrink={0} />;
  const previewBtn = (p: Proposal) => (
    <Tooltip label="Preview" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Preview" icon={<FiEye />} onClick={() => previewProposal(p.id)} />
    </Tooltip>
  );
  const downloadBtn = (p: Proposal) => (
    <Tooltip label="Download PDF" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Download PDF" icon={<FiDownload />} onClick={() => downloadProposal(p.id)} />
    </Tooltip>
  );

  const rowActions = (p: Proposal): React.ReactNode => {
    // item 553 — the Archived filter view: Restore in the primary slot (blue) + Preview/Download,
    // aligned to the same columns as active rows.
    if (showingArchived) {
      // dart (2026-07-16, Chad) — a void+archived quote gets ONE-CLICK full restore (clears both →
      // active); an archived-only quote just un-archives. Either way it lands active, so label "Restore".
      const bothStates = p.isVoid;
      return (
        <HStack spacing={1} justify="flex-end">
          {canModerate && (
            <Tooltip label={bothStates ? 'Un-archive and un-void → active' : 'Un-archive → active'} openDelay={400}>
              <Button
                size="xs"
                minW="76px"
                colorScheme="blue"
                isLoading={(bothStates ? fullyRestoringId : restoringId) === p.id}
                loadingText="…"
                onClick={() => doRestore(p, bothStates)}
              >
                Restore
              </Button>
            </Tooltip>
          )}
          {iconSpacer}
          {previewBtn(p)}
          {downloadBtn(p)}
          {iconSpacer}
        </HStack>
      );
    }
    // item 549/553 — a VOID proposal STAYS in the list; Restore takes the PRIMARY-action slot
    // (same size as Approve, a distinct colour), and Preview/Download keep their normal columns.
    if (p.isVoid) {
      return (
        <HStack spacing={1} justify="flex-end">
          {canModerate && <Button size="xs" minW="76px" colorScheme="blue" isLoading={unvoidingId === p.id} loadingText="…" onClick={() => tryUnvoid(p.id)}>Restore</Button>}
          {iconSpacer}
          {previewBtn(p)}
          {downloadBtn(p)}
          {iconSpacer}
        </HStack>
      );
    }
    const prim = primaryAction(p);
    const canDecline = p.status === ProposalStatus.Pending || p.status === ProposalStatus.Approved;
    // item 553 — an ACCEPTED proposal (Approved onward) can't be voided, only archived (Chad);
    // Void stays available only while Pending or Declined.
    const canVoid = p.status === ProposalStatus.Pending || p.status === ProposalStatus.Declined;
    return (
      <HStack spacing={1} justify="flex-end">
        {prim && <Button size="xs" minW="76px" colorScheme="brandGreen" onClick={prim.onClick}>{prim.label}</Button>}
        <Tooltip label="Duplicate" openDelay={300}>
          {/* item 681 (Chad) — engine proposals get the guided CFG choice; standard ones duplicate directly */}
          <IconButton size="xs" variant="ghost" aria-label="Duplicate" icon={<FiCopy />} isLoading={duplicatingId === p.id} onClick={() => (isEngineProposal(p) ? setDupChoice(p) : duplicateProposal(p.id))} />
        </Tooltip>
        {previewBtn(p)}
        {downloadBtn(p)}
        <Menu isLazy>
          <MenuButton as={IconButton} size="xs" variant="ghost" aria-label="More actions" icon={<FiMoreVertical />} />
          <Portal>
            <MenuList fontSize="sm">
              <MenuItem icon={<FiEdit2 />} onClick={() => tryEdit(p.id)}>Edit</MenuItem>
              {canDecline && <MenuItem onClick={() => tryChangeStatus(p.id, ProposalStatus.Declined)}>Decline</MenuItem>}
              {/* item 576 — Send is offered at ANY status (matching the form footer): Pending
                  carries the accept link; later statuses send status-aware copy instead.
                  item 1031 — an unsent customer request spells out that sending also publishes
                  pricing to the customer's external portal. */}
              <MenuItem onClick={() => sendToClient(p)}>
                {p.isCustomerSubmission && !(p as any).sentDate ? 'Send & publish to portal' : 'Send to client'}
              </MenuItem>
              {/* item 737 (Chad) — enroll a sold subscription right here (also lives on the
                  Recurring page + the project's Subscriptions section). Only when this proposal
                  carries a recurring sale that isn't enrolled yet, and only for admins. */}
              {isAdmin && (p as any).pendingRecurring && !p.isVoid && !isArchivedRow(p) && (
                <MenuItem icon={<FiRepeat />} onClick={() => setEnrollProposalId(p.id)}>Enroll subscription</MenuItem>
              )}
              {isEngineProposal(p) && (
                <MenuItem icon={<FiDownload />} onClick={() => exportStandard(p)}>Export standard proposal</MenuItem>
              )}
              {/* item 1142 (Chad) — flag THIS deal as an enterprise deploy (team-effort comp:
                  finalize reminds the booker to zero non-sales commissions) */}
              {isAdmin && (
                <MenuItem onClick={() => toggleEnterprise(p)}>
                  {(p as any).isEnterprise ? 'Unflag enterprise deploy' : 'Flag as enterprise deploy'}
                </MenuItem>
              )}
              <MenuItem onClick={() => openCopyModal(p.id)}>Copy to project…</MenuItem>
              {/* item 843 (slice C) — Pulse review actions for a customer submission */}
              {isAdmin && p.isCustomerSubmission && (
                <MenuItem icon={<FiCornerUpRight />} onClick={() => openMoveModal(p)}>Move to project…</MenuItem>
              )}
              {isAdmin && p.isCustomerSubmission && p.customerSubmissionState === CustomerSubmissionState.Submitted && (
                <MenuItem onClick={() => doReopen(p.id)}>Reopen for customer edits</MenuItem>
              )}
              {/* item 1473 — a Pending proposal adopts a standalone work order (moderators) */}
              {canModerate && p.status === ProposalStatus.Pending && !p.isCustomerSubmission && (
                <MenuItem icon={<FiLink />} onClick={() => openAttach(p)}>Attach existing work order…</MenuItem>
              )}
              {/* item 1469 — the void-and-re-run loop's missing exit */}
              {canModerate && (p.status === ProposalStatus.Approved || p.status === ProposalStatus.ApprovedDeposited) && (
                <MenuItem icon={<FiRotateCcw />} onClick={() => doReopenPending(p)}>Reopen as Pending</MenuItem>
              )}
              {/* item 1500 (Chad) — the row ⋮ Void/Archive fired with NO confirm (the bulk bar asks;
                  one row didn't). Same shared dialog + wording as the bulk bar; a proposal void is
                  reversible (item 1208 Restore), so say so rather than claim it's permanent. */}
              {canModerate && canVoid && (
                <MenuItem
                  onClick={() => { tryVoid(p.id); }} // EPIC_VOID_FLOW — the shared dialog asks
                >
                  Void
                </MenuItem>
              )}
              {canModerate && canArchiveProposal(p) && (
                <MenuItem
                  onClick={async () => {
                    if (!(await confirm(
                      `Archive ${p.proposalNumber || 'this proposal'}? It leaves the ledger (the Archived filter still shows it).`,
                      { title: 'Archive proposal', confirmLabel: 'Archive', destructive: true },
                    ))) return;
                    tryArchive(p.id);
                  }}
                >
                  Archive
                </MenuItem>
              )}
            </MenuList>
          </Portal>
        </Menu>
      </HStack>
    );
  };

  // checkboxes → BULK housekeeping only (archive / void); never bulk approve/convert
  const bulkActions: LedgerBulkAction<Proposal>[] = canModerate ? [
    {
      label: 'Archive',
      appliesTo: (p) => !isArchivedRow(p) && canArchiveProposal(p), // item 1570 — void first
      confirm: (rows) => `Archive ${rows.length} proposal(s)?`,
      onRun: async (rows) => { await Promise.all(rows.map((p) => tryArchive(p.id))); },
    },
    {
      label: 'Void',
      colorScheme: 'red',
      // item 553 — only Pending/Declined can be voided; accepted proposals archive only.
      appliesTo: (p) => !p.isVoid
        && (p.status === ProposalStatus.Pending || p.status === ProposalStatus.Declined),
      onRun: async (rows) => { await voidMany(rows.map((p) => p.id)); }, // EPIC_VOID_FLOW — one dialog (reason + notify)
    },
  ] : [];

  const rows = useMemo(() => {
    // item 553 — the "Archived" filter shows archived proposals (fetched separately), scoped to
    // this project when in a project. Every other filter shows active + void rows (void stays in
    // the list, marked, with a Restore action).
    if (statusFilter === 'archived') {
      return (archived as Proposal[]).filter((p) => !inProject || p.projectId === projectId);
    }
    // dart (2026-07-16, Chad) — the project detail includes archived proposals; keep them OUT of the
    // active list (they belong in the Archived filter). Backend sends `isArchive`; the model calls it
    // `isArchived` — the old check only tested `isArchived`, so archived rows leaked into "active".
    const list = (data ?? []).filter((p) => !isArchivedRow(p));
    return statusFilter === 'all' ? list : list.filter((p) => String(p.status) === statusFilter);
  }, [data, statusFilter, archived, inProject, projectId]);

  const copyToProject = async () => {
    if (!copyProjectId) return;
    try {
      const original = await fetchById(copyId);
      if (!original) return;
      const copy = { ...original };
      copy.id = NIL;
      copy.projectId = copyProjectId;
      copy.date = localToday(); // dart (2026-07-15, Chad) — NEXT_EPIC #5: a copy dates to TODAY
      // dart (2026-07-15, Chad) — versioned naming instead of "COPY of …"
      copy.proposalTitle = nextVersionName(copy.proposalTitle ?? '', (data ?? []).map((p) => p.proposalTitle ?? ''));
      copy.customerProjectManagerId = undefined;
      copy.proposalItems.forEach((i) => { i.id = v4(); });
      copy.status = ProposalStatus.Pending;
      copy.isVoid = false;
      copy.isArchived = false;
      copy.isLocked = false;
      await tryAddProposal(copy);
      toast({ status: 'success', title: 'Copied to project' });
    } catch {
      toast({ status: 'error', title: 'Action failed', description: 'The document was NOT saved.' });
    }
    copyModal.onClose();
  };

  return (
    <>
      <DocumentGridShell
        heading="PROPOSALS"
        icon={IconProposals}
        addPath={`/proposal/${NIL}${inProject ? `?projectId=${projectId}` : ''}`}
        collapsible={!!collapse}
        collapsed={collapse?.collapsed}
        onToggleCollapse={collapse?.onToggle}
        status={status}
        data={data === undefined ? undefined : rows}
      >
        {/* item 712 (Chad) — the header "N records" must match the VISIBLE list. This grid renders
            its own filtered body (archived hidden), so the shell is fed the filtered `rows` for its
            count instead of the raw `data` (which still counted archived → "5 records" vs "1 item").
            `data` stays undefined while loading so the shell shows skeletons, not "0 records". */}
        <LedgerTable<Proposal>
          defaultSort={{ key: 'created', dir: -1 }} // item 1331 (Chad) — DATE order, newest on top (number order lies for revisions)
          rows={rows}
          columns={columns}
          rowSubline={(p) => (
            <LedgerSubline
              name={p.proposalTitle}
              meta={[
                ...(inProject ? [] : [(p as any).customerName, (p as any).projectName]),
                // item 1475/1475b — the deal's lead(s): the row's own names (index rows), else the
                // global proposals index, else the WO index (the in-project grid gets RAW entities,
                // which carry no projectLeadNames — Palms PROP-100385 showed nothing)
                leadLineFor(p),
              ]}
            />
          )}
          rowId={(p) => p.id}
          rowActions={rowActions}
          rowActionsWidth={LEDGER_W.actions}
          rowActionsFlex
          rowActionsHeader="ACTION"
          bulkActions={showingArchived ? [] : bulkActions}
          selectable={canModerate && !showingArchived}
          filters={(
            <Select size="sm" maxW="200px" variant="filled" borderRadius="lg" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">ALL STATUSES</option>
              {Object.entries(PROPOSAL_STATUS_LABELS).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
              {/* item 553 — archived proposals as a filter view, not a peer section */}
              <option value="archived">ARCHIVED</option>
            </Select>
          )}
          searchText={(p) => `${p.proposalNumber ?? ''} ${p.proposalTitle ?? ''} ${(p as any).customerName ?? ''}`}
          searchPlaceholder="Search proposals…"
          onRowClick={showingArchived ? undefined : (p) => tryEdit(p.id)}
          emptyText={showingArchived ? 'No archived proposals.' : 'No proposals yet.'}
        />
      </DocumentGridShell>
      {/* item 681 (Chad) — guided duplicate for engine (RNWY) proposals: keep the frozen CFG (exact
          clone) or refresh to the current Settings CFG (current pricing + latest Sources/Equipment). */}
      <Modal isOpen={!!dupChoice} onClose={() => setDupChoice(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Duplicate proposal</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              {`Duplicating "${dupChoice?.proposalTitle || 'this proposal'}". Venues and scope carry over either way — choose whether the copy keeps this quote's rate card and RNWY standardized language, or pulls the current Settings:`}
            </Text>
            {/* item 762 (Chad) — the two options were shown TWICE (inert description cards + a
                second row of buttons repeating them). Now the cards ARE the choice: click one. */}
            <Box
              as="button"
              type="button"
              w="100%"
              textAlign="left"
              borderWidth="1px"
              borderRadius="md"
              p={3}
              mb={2}
              _hover={{ borderColor: 'brandGreen.400', bg: 'whiteAlpha.100' }}
              onClick={() => { const p = dupChoice; setDupChoice(null); if (p) duplicateProposal(p.id, false); }}
            >
              <Text fontSize="sm" fontWeight="semibold">Keep this quote&apos;s pricing &amp; settings</Text>
              <Text fontSize="xs" color="gray.500">Exact clone — same rate card and any per-proposal adjustments.</Text>
            </Box>
            <Box
              as="button"
              type="button"
              w="100%"
              textAlign="left"
              borderWidth="1px"
              borderRadius="md"
              p={3}
              _hover={{ borderColor: 'brandGreen.400', bg: 'whiteAlpha.100' }}
              onClick={() => { const p = dupChoice; setDupChoice(null); if (p) duplicateProposal(p.id, true); }}
            >
              <Text fontSize="sm" fontWeight="semibold">Refresh to current settings</Text>
              <Text fontSize="xs" color="gray.500">
                Re-pull the live Settings rate card — current pricing plus your latest Sources &amp; Equipment
                lists (useful for older quotes that predate them).
                — plus the current RNWY standardized language from Settings.
              </Text>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setDupChoice(null)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ProposalApprovalWizard isOpen={approvalModal.isOpen} onClose={approvalModal.onClose} proposal={approving} />
      <ProposalCompletionWizard isOpen={completionModal.isOpen} onClose={completionModal.onClose} proposal={completing} />
      {/* item 550/576 — the real-email "Send to client" dialog, opened programmatically from
          the row menu; ProposalSendControl is the SAME flow the proposal form footer uses
          (accept link when Pending, status-aware server copy otherwise) */}
      <ProposalSendControl
        hideTrigger
        proposalId={sendId}
        status={sendStatus}
        autoOpenSignal={sendSignal}
      />
      {/* item 549 — clean copy-to-project: pick an existing project (was a janky free-text slide-down) */}
      <Modal isOpen={copyModal.isOpen} onClose={copyModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Copy proposal to another project</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Destination project</FormLabel>
              <Select placeholder="Select a project" value={copyProjectId} onChange={(e) => setCopyProjectId(e.target.value)}>
                {/* item 1454 — grouped under the CUSTOMER so same-named projects are unambiguous */}
                <ProjectOptionsByCustomer projects={projects?.index} />
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={copyModal.onClose}>Cancel</Button>
            <Button colorScheme="brandGreen" isDisabled={!copyProjectId} onClick={copyToProject}>Copy</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* item 843 (slice C) — MOVE a customer submission to a live project (same customer only) */}
      <Modal isOpen={moveModal.isOpen} onClose={moveModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Move proposal to a live project</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3} opacity={0.8}>
              Re-files this one proposal under another of the customer&apos;s projects. The customer
              still sees it in their portal.
            </Text>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Destination project</FormLabel>
              <Select placeholder="Select a project" value={moveProjectId} onChange={(e) => setMoveProjectId(e.target.value)}>
                {(projects?.index ?? [])
                  .filter((pr) => (pr as any).customerId === moveCustomerId)
                  .map((pr) => (
                    <option key={pr.id} value={pr.id}>{pr.name}</option>
                  ))}
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={moveModal.onClose}>Cancel</Button>
            <Button colorScheme="brandGreen" isDisabled={!moveProjectId} onClick={doMove}>Move</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* item 737 (Chad) — enroll a sold subscription from the proposal ⋮ menu */}
      {/* item 1473 — Attach existing work order… */}
      <Modal isOpen={!!attachFor} onClose={() => (attachBusy ? undefined : setAttachFor(null))} size="2xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{`Attach existing work order — ${attachFor?.proposalNumber ?? ''}`}</ModalHeader>
          <ModalCloseButton isDisabled={attachBusy} />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              For a job whose work order was made by hand (legacy paper) while the proposal stayed Pending. Pick the
              project&apos;s standalone work order; its live invoices follow it, and the proposal lands on the status its
              billing has already reached. Nothing is regenerated, booked or sent.
            </Text>
            {attachRows.length === 0 && <Text fontSize="sm" color="gray.500">No standalone work orders on this project.</Text>}
            <VStack align="stretch" spacing={2}>
              {attachRows.map((r) => (
                <Box
                  key={r.id}
                  borderWidth="1px"
                  borderColor={attachPick === r.id ? 'brandGreen.400' : undefined}
                  borderRadius="md"
                  p={3}
                  cursor={r.foreignInvoices > 0 ? 'not-allowed' : 'pointer'}
                  opacity={r.foreignInvoices > 0 ? 0.5 : 1}
                  onClick={() => { if (r.foreignInvoices === 0) setAttachPick(r.id); }}
                >
                  <HStack spacing={3} align="baseline" flexWrap="wrap">
                    <Text fontWeight="bold" fontFamily="mono">{r.workOrderNumber}</Text>
                    {r.isSalesOrder && <Badge variant="subtle">SO</Badge>}
                    <Text fontSize="sm" noOfLines={1} flex={1} minW={0}>{r.title || '—'}</Text>
                    <Badge colorScheme={r.amountMatches ? 'green' : 'orange'} variant="subtle">
                      {r.amountMatches ? 'AMOUNT MATCHES' : `WO ${money(r.total)} · PROPOSAL ${money(r.proposalTotal)}`}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {`made by ${r.createdBy ?? '—'} on ${new Date(r.createdDate).toLocaleDateString()} · ${r.liveInvoices.length} live invoice${r.liveInvoices.length === 1 ? '' : 's'}${r.liveInvoices.length ? ` (${r.liveInvoices.join(', ')})` : ''} · proposal would read ${STATUS_WORD[r.resultingStatus] ?? 'Approved'}`}
                    {r.foreignInvoices > 0 && ' · has invoices on another proposal — cannot attach'}
                  </Text>
                </Box>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setAttachFor(null)} isDisabled={attachBusy}>Cancel</Button>
            <Button onClick={doAttach} isLoading={attachBusy} isDisabled={!attachPick}>Attach</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <EnrollSubscriptionModal
        proposalId={enrollProposalId}
        isOpen={!!enrollProposalId}
        onClose={() => setEnrollProposalId(undefined)}
      />
    </>
  );
};

ProposalsGrid.defaultProps = {
  data: undefined,
  projectId: NIL,
  collapse: undefined,
};

export default ProposalsGrid;
